'use client';

import { useEffect, useState } from 'react';
import { Crown, Loader2, LogIn, RotateCcw, Sparkles, Smartphone } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { Modal } from '@/components/ui/Modal';
import { FantasyButton } from '@/components/ui/FantasyButton';
import AuthModal from '@/components/auth/AuthModal';
import { useAuth } from '@/context/AuthContext';
import { fetchAccountLicense, getTvProductPrice, purchaseTvOnPhone, restoreTvOnPhone } from '@/lib/tvPhoneBilling';

/**
 * Telefon: „Tryb TV" na koncie gracza. Licencja wymaga konta (gość → logowanie), zakup idzie
 * przez Google Play w aplikacji na Androida (TWA), poza nią — wskazówka, gdzie kupić.
 * Kupiony Tryb TV działa na każdym ekranie, który zakłada ten gracz (Android TV, Chromecast).
 */
export function TvBuyModal({ isOpen, onClose }) {
  const t = useTranslations('Tv');
  const { user } = useAuth();
  const loggedIn = !!user?._id;
  const [authView, setAuthView] = useState(null);
  const [license, setLicense] = useState(null);
  const [price, setPrice] = useState(undefined); // undefined = sprawdzam, null = brak Play Billing
  const [status, setStatus] = useState(null); // 'buying' | 'restoring' | kod błędu
  const busy = status === 'buying' || status === 'restoring';

  useEffect(() => {
    if (!isOpen || !loggedIn) return;
    let cancelled = false;
    fetchAccountLicense().then((l) => { if (!cancelled) setLicense(l); });
    getTvProductPrice().then((p) => { if (!cancelled) setPrice(p); });
    return () => { cancelled = true; };
  }, [isOpen, loggedIn]);

  const run = async (kind, fn) => {
    setStatus(kind);
    try {
      setLicense(await fn());
      setStatus(null);
    } catch (e) {
      setStatus(e?.code === 'CANCELED' ? null : (e?.code || 'VERIFY_FAILED'));
    }
  };

  const errorText = status && !busy
    ? ({
        PENDING: t('purchase_pending'),
        TOKEN_IN_USE: t('tv_buy_token_in_use'),
        NOT_FOUND: t('tv_buy_not_found'),
        LOGIN_REQUIRED: t('tv_buy_login_text'),
      }[status] || t('purchase_verify_failed'))
    : null;

  let body;
  if (!loggedIn) {
    body = (
      <>
        <p className="text-sm text-[var(--text-secondary)] text-center">{t('tv_buy_login_text')}</p>
        <FantasyButton onClick={() => setAuthView('LOGIN')} accent="gold" icon={LogIn} className="w-full">
          {t('tv_buy_login')}
        </FantasyButton>
        <button onClick={() => setAuthView('REGISTER')} className="text-xs text-[var(--text-muted)] hover:text-[var(--gold-light)] underline">
          {t('tv_buy_register')}
        </button>
      </>
    );
  } else if (license?.licensed) {
    body = (
      <p className="text-sm text-[var(--text-secondary)] text-center flex items-center gap-2">
        <Crown className="w-5 h-5 text-[var(--gold-primary)] shrink-0" /> {t('tv_buy_owned')}
      </p>
    );
  } else if (price === undefined) {
    body = <Loader2 className="w-6 h-6 animate-spin text-[var(--text-muted)]" />;
  } else if (price === null) {
    body = (
      <p className="text-sm text-[var(--text-secondary)] text-center flex items-start gap-2">
        <Smartphone className="w-5 h-5 text-[var(--gold-primary)] shrink-0 mt-0.5" /> {t('tv_buy_in_app')}
      </p>
    );
  } else {
    body = (
      <>
        <p className="text-sm text-[var(--text-secondary)] text-center">{t('tv_buy_text')}</p>
        {license && !license.licensed && license.enforced && (
          <p className="text-xs text-[var(--text-gold)]">{t('license_demo_left', { count: license.demoLeft ?? 0 })}</p>
        )}
        <FantasyButton onClick={() => run('buying', purchaseTvOnPhone)} disabled={busy} accent="gold" icon={status === 'buying' ? Loader2 : Sparkles} className="w-full">
          {t('tv_buy_button', { price })}
        </FantasyButton>
        <button onClick={() => run('restoring', restoreTvOnPhone)} disabled={busy} className="inline-flex items-center gap-1.5 text-xs text-[var(--text-muted)] hover:text-[var(--gold-light)]">
          <RotateCcw className="w-3.5 h-3.5" /> {t('license_restore')}
        </button>
      </>
    );
  }

  return (
    <>
      <Modal isOpen={isOpen && !authView} onClose={onClose} title={t('tv_buy_title')}>
        <div className="flex flex-col items-center gap-4 py-2">
          {body}
          {errorText && (
            <div className="w-full rounded-xl bg-red-500/10 border border-red-500/30 px-4 py-2.5 text-sm text-red-300 text-center">{errorText}</div>
          )}
        </div>
      </Modal>
      {authView && <AuthModal isOpen onClose={() => setAuthView(null)} initialView={authView} />}
    </>
  );
}

export default TvBuyModal;
